import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";
import { X, Phone, Mail } from "lucide-react";
import { useI18n } from "../contexts/I18nContext";
import { GALLERY } from "../data/products";
import { GALLERY_PAGE_SEO, GALLERY_SEO_ITEMS } from "../data/gallerySeoContent";
import { buildGalleryJsonLd } from "../lib/gallerySeo";

/** Met à jour ou crée une meta tag dans le <head>. */
function upsertMeta(name, content) {
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
  return el;
}

export default function Gallery() {
  const { lang } = useI18n();
  const [active, setActive] = useState(null);
  const location = useLocation();

  // Ouvre + scroll vers l'item ciblé par l'URL hash (#gallery-item-N).
  // Utilisé par la barre de recherche du site pour envoyer directement à
  // l'image trouvée. La lightbox s'ouvre automatiquement.
  useEffect(() => {
    const hash = (location.hash || "").replace(/^#gallery-item-/, "");
    const idx = Number.parseInt(hash, 10);
    if (Number.isNaN(idx) || idx < 0 || idx >= GALLERY.length) return;
    const timer = setTimeout(() => {
      const el = document.getElementById(`gallery-item-${idx}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      const seoItem = GALLERY_SEO_ITEMS[idx] || {};
      setActive({ ...GALLERY[idx], seoItem, index: idx });
    }, 250);
    return () => clearTimeout(timer);
  }, [location.hash]);

  // Title + meta description dynamiques (SEO). Restauré au démontage.
  useEffect(() => {
    const prevTitle = document.title;
    const prevMeta = document.querySelector('meta[name="description"]')?.getAttribute("content") || "";
    document.title = GALLERY_PAGE_SEO.title;
    upsertMeta("description", GALLERY_PAGE_SEO.metaDescription);
    return () => {
      document.title = prevTitle;
      if (prevMeta) upsertMeta("description", prevMeta);
    };
  }, []);

  // JSON-LD ImageGallery
  useEffect(() => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "gallery-jsonld";
    // On enrichit le JSON-LD avec les seoTitle + descriptions détaillées
    const jsonLd = buildGalleryJsonLd(GALLERY, lang);
    jsonLd.image = jsonLd.image.map((img, i) => ({
      ...img,
      name: GALLERY_SEO_ITEMS[i]?.seoTitle || img.name,
      description: GALLERY_SEO_ITEMS[i]?.description || img.description,
    }));
    script.text = JSON.stringify(jsonLd);
    document.head.appendChild(script);
    return () => document.getElementById("gallery-jsonld")?.remove();
  }, [lang]);

  // Escape ferme la lightbox
  useEffect(() => {
    if (!active) return;
    const onKey = (e) => { if (e.key === "Escape") setActive(null); };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [active]);

  const seo = GALLERY_PAGE_SEO;

  return (
    <div data-testid="gallery-page">
      {/* HEADER SEO */}
      <section className="relative py-20 border-b border-white/5">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
          <span className="label-tech text-[#39FF14]">Galerie · Signapharma</span>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-bold uppercase tracking-tight leading-[1.05]">
            <span className="chrome-text">{seo.h1}</span>
          </h1>
          <div className="mt-8 space-y-5 text-zinc-300 font-body text-base leading-relaxed max-w-4xl">
            {seo.intro.map((p, i) => (
              <p key={i} data-testid={`gallery-intro-p-${i}`}>{p}</p>
            ))}
          </div>
          <p className="mt-6 text-zinc-500 font-mono text-xs tracking-wide">
            Croix de pharmacie · Croix lumineuse · Enseigne lumineuse pharmacie · Relief lumineux pharmacie
            <span className="text-[#39FF14]"> · </span>Genève · Carouge · Onex · Petit-Lancy · Cologny · Lausanne · Sion · Neuchâtel · Fribourg · Valais · Jura
          </p>
        </div>
      </section>

      {/* GRILLE GALERIE */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="gallery-grid">
          {GALLERY.map((g, i) => {
            const seoItem = GALLERY_SEO_ITEMS[i] || {};
            const title = seoItem.seoTitle || g.title[lang] || g.title.fr;
            const alt = seoItem.alt || title;
            const legend = seoItem.legend || "";
            const description = seoItem.description || "";
            return (
              <figure
                key={i}
                id={`gallery-item-${i}`}
                itemScope
                itemType="https://schema.org/ImageObject"
                className="m-0 scroll-mt-24"
              >
                <button
                  onClick={() => setActive({ ...g, seoItem, index: i })}
                  data-testid={`gallery-item-${i}`}
                  aria-label={alt}
                  title={title}
                  className="group relative aspect-[4/3] w-full overflow-hidden border border-white/5 hover:border-[#39FF14]/60 transition-all block"
                >
                  <img
                    src={g.url}
                    alt={alt}
                    title={title}
                    itemProp="contentUrl"
                    loading={i < 3 ? "eager" : "lazy"}
                    decoding="async"
                    fetchPriority={i < 3 ? "high" : "auto"}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent opacity-90 group-hover:opacity-70 transition-opacity" />
                  <h2
                    className="absolute bottom-0 left-0 right-0 p-4 font-display text-sm font-semibold text-white group-hover:text-[#39FF14] transition-colors leading-snug"
                    itemProp="name"
                  >
                    {title}
                  </h2>
                </button>
                {/* Légende visible sous chaque image */}
                {legend && (
                  <figcaption
                    className="mt-3 text-zinc-400 font-body text-xs leading-relaxed"
                    itemProp="caption"
                    data-testid={`gallery-legend-${i}`}
                  >
                    {legend}
                  </figcaption>
                )}
                {/* Description technique cachée visuellement mais indexable */}
                {description && (
                  <span className="sr-only" itemProp="description">
                    {description}
                  </span>
                )}
              </figure>
            );
          })}
        </div>
      </section>

      {/* TEXTE SEO APRÈS GALERIE */}
      <section className="border-t border-white/5 bg-[#080808] py-20">
        <div className="max-w-[1000px] mx-auto px-6 lg:px-10 space-y-14 text-zinc-300 font-body leading-relaxed">
          {/* Fabrication sur mesure */}
          <div>
            <h2 className="font-display text-2xl sm:text-3xl font-bold uppercase tracking-tight text-white mb-5">
              {seo.afterGallery.heading1}
            </h2>
            <p className="mb-4">{seo.afterGallery.p1}</p>
            <p className="mb-3 text-zinc-400 text-sm font-mono uppercase tracking-widest">
              {seo.afterGallery.listIntro}
            </p>
            <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm text-zinc-300">
              {seo.afterGallery.list.map((item, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="text-[#39FF14] shrink-0 mt-0.5">›</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Pose dans toute la Suisse */}
          <div>
            <h2 className="font-display text-2xl sm:text-3xl font-bold uppercase tracking-tight text-white mb-5">
              {seo.afterGallery.heading2}
            </h2>
            <p className="mb-4">{seo.afterGallery.p2}</p>
            <p>{seo.afterGallery.p3}</p>
          </div>

          {/* CTA */}
          <div className="border border-[#39FF14]/30 bg-black/40 p-8 sm:p-10">
            <h2 className="font-display text-2xl sm:text-3xl font-bold uppercase tracking-tight text-white mb-4">
              {seo.afterGallery.ctaHeading}
            </h2>
            <p className="mb-6 text-zinc-300">{seo.afterGallery.ctaText}</p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#39FF14] text-black font-mono text-xs uppercase tracking-widest hover:bg-white transition-colors"
                data-testid="gallery-cta-contact"
              >
                <Mail size={14} /> Demander un devis
              </Link>
              <a
                href="tel:+41762223603"
                className="inline-flex items-center gap-2 px-6 py-3 border border-[#39FF14] text-[#39FF14] font-mono text-xs uppercase tracking-widest hover:bg-[#39FF14] hover:text-black transition-colors"
                data-testid="gallery-cta-phone"
              >
                <Phone size={14} /> +41 76 222 36 03
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* LIGHTBOX (portal) */}
      {active && createPortal(
        <div
          className="fixed inset-0 bg-black/92 backdrop-blur-md z-[9999] flex flex-col items-center justify-center p-4 sm:p-8"
          onClick={() => setActive(null)}
          data-testid="gallery-lightbox"
          role="dialog"
          aria-modal="true"
        >
          <button
            onClick={(e) => { e.stopPropagation(); setActive(null); }}
            className="absolute top-6 right-6 p-3 text-white hover:text-[#39FF14] transition-colors z-[10000] bg-black/70 border border-white/10 hover:border-[#39FF14]/60"
            data-testid="lightbox-close"
            aria-label="Fermer"
          >
            <X size={22} />
          </button>
          <div className="max-w-5xl w-full flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
            <img
              src={active.url}
              alt={active.seoItem?.alt || active.title?.fr || ""}
              title={active.seoItem?.seoTitle || ""}
              className="max-w-full max-h-[65vh] object-contain drop-shadow-[0_0_60px_rgba(57,255,20,0.3)]"
            />
            {active.seoItem?.seoTitle && (
              <h3 className="mt-6 font-display text-lg sm:text-xl font-semibold text-white text-center max-w-3xl">
                {active.seoItem.seoTitle}
              </h3>
            )}
            {active.seoItem?.legend && (
              <p className="mt-3 text-zinc-400 text-sm text-center max-w-3xl leading-relaxed">
                {active.seoItem.legend}
              </p>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
